import { createApi } from "@reduxjs/toolkit/query/react";
import {
  baseQueryWithArgs,
  getQueryStringWithCreator,
  postQueryBodyWithCreator,
} from "../../../utils/reduxHelper";
import { getSearchId, triggerGa } from "../../../utils/gaUtils";
import {
  GA_ACTION,
  GA_CATEGORIES,
  GA_PAGE,
  GA_SCREEN_NAME,
} from "../../../utils/constants";

export const sourcingHomeReduxAPI = createApi({
  reducerPath: "sourcingHomeReduxAPI",
  baseQuery: baseQueryWithArgs,
  tagTypes: [
    "categories",
    "sourceByPriceCollections",
    "recommendedCollections",
    "subcategories",
    "brandsMain",
    "hottestDeals",
    "homeSearchBrands",
    "homeSearchProducts",
    "brandSubcategories",
    "activeOrders",
    "barterCollection",
  ],

  endpoints: (builder) => ({
    //GET Queries
    getCategories: builder.query({
      query: () => getQueryStringWithCreator(`s/getCategories`),
      providesTags: ["categories"],
    }),
    getCollectionsByPrice: builder.query({
      query: () => getQueryStringWithCreator(`s/getCollectionsByPrice`),
      providesTags: ["sourceByPriceCollections"],
    }),
    getRecommendedCollections: builder.query({
      query: () => getQueryStringWithCreator(`s/getRecommendedCollections`),
      providesTags: ["recommendedCollections"],
    }),
    getSubcategoriesFromCategory: builder.query({
      query: (categoryId) =>
        getQueryStringWithCreator(
          `s/getSubcategoriesFromCategory?categoryId=${categoryId}`
        ),
      providesTags: ["subcategories"],
      keepUnusedDataFor: 60,
    }),
    getSubcategoriesForBrand: builder.query({
      query: ({ brandId, isBrandSourcing = false }) =>
        getQueryStringWithCreator(
          `s/getSubcategoriesForBrand?brandId=${brandId}&isBrandSourcing=${isBrandSourcing}`
        ),
      providesTags: ["brandSubcategories"],
      keepUnusedDataFor: 60,
    }),
    getBrandsMainForHome: builder.query({
      query: () => getQueryStringWithCreator(`s/getBrandsMainForHome`),
      providesTags: ["brandsMain"],
    }),
    getBrandsAndCategoriesForHome: builder.query({
      query: () => getQueryStringWithCreator(`s/getBrandsAndCategoriesForHome`),
      providesTags: ["brandsMain", "categories"],
    }),
    getBarterCollection: builder.query({
      query: () => getQueryStringWithCreator(`s/getBarterCollections`),
      providesTags: ["brandsMain"],
    }),
    getBrandsEndlessForHome: builder.query({
      query: ({ page }) => {
        return getQueryStringWithCreator(
          `s/getBrandsEndlessForHome?page=${page}`
        );
      },
      providesTags: ["homeSearchProducts"],
    }),
    getBrandsAndSubcategoriesForHome: builder.query({
      query: () => {
        return getQueryStringWithCreator(`s/getBrandsAndSubcategoriesForHome`);
      },
      providesTags: ["brandsWithSubcategories"],
    }),
    getHottestDealsForHome: builder.query({
      query: () => getQueryStringWithCreator(`s/getHottestDealsForHome`),
      providesTags: ["hottestDeals"],
    }),
    getHomeSearchBrands: builder.query({
      query: ({ searchText, page }) => {
        const search = encodeURIComponent(searchText);
        return getQueryStringWithCreator(
          `s/getHomeSearchBrands?search=${search}`
        );
      },
      providesTags: ["homeSearchBrands"],
    }),
    getHomeSearchProducts: builder.query({
      query: ({ searchText, page }) => {
        const search = encodeURIComponent(searchText);
        return getQueryStringWithCreator(
          `s/getHomeSearchProducts?search=${search}&page=${page}`
        );
      },
      transformResponse: (response, meta, arg) => {
        const search = (arg?.searchText || "").trim();
        if (
          (arg?.page || 1) === 1 &&
          search &&
          response?.success &&
          arg?.searchId &&
          arg.searchId === getSearchId("home")
        ) {
          triggerGa({
            category: GA_CATEGORIES.USER_INTERACTION,
            action: GA_ACTION.SEARCH_RESULTS_LOAD,
            label: search,
            label2: String(response?.num_pages ?? ""),
            page: GA_PAGE.WISHLINK_SOURCING,
            screenName: GA_SCREEN_NAME.SEARCH_PLP,
            eventValue: response?.total_count ?? response?.data?.length ?? 0,
            searchSurface: "home",
          });
        }
        return response;
      },
      providesTags: ["homeSearchProducts"],
    }),
    getActiveOrders: builder.query({
      query: () => {
        return getQueryStringWithCreator(`s/getActiveOrders`);
      },
      providesTags: ["activeOrders"],
    }),
    markSourcingEntitySeen: builder.mutation({
      query: ({ entityType, entityId }) => ({
        url: `/c/markSourcingEntitySeen`,
        method: "POST",
        body: postQueryBodyWithCreator({
          entity_type: entityType,
          entity_id: entityId,
        }),
      }),
      invalidatesTags: ["recommendedCollections", "hottestDeals"],
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useLazyGetCategoriesQuery,
  useLazyGetCollectionsByPriceQuery,
  useLazyGetSubcategoriesFromCategoryQuery,
  useLazyGetSubcategoriesForBrandQuery,
  useLazyGetRecommendedCollectionsQuery,
  useLazyGetBrandsMainForHomeQuery,
  useLazyGetBrandsAndCategoriesForHomeQuery,
  useLazyGetBrandsEndlessForHomeQuery,
  useLazyGetBrandsAndSubcategoriesForHomeQuery,
  useLazyGetHottestDealsForHomeQuery,
  useLazyGetHomeSearchBrandsQuery,
  useLazyGetHomeSearchProductsQuery,
  useLazyGetActiveOrdersQuery,
  useGetSubcategoriesForBrandQuery,
  useGetSubcategoriesFromCategoryQuery,
  useGetBarterCollectionQuery,
  useGetRecommendedCollectionsQuery,
  useMarkSourcingEntitySeenMutation,
} = sourcingHomeReduxAPI;
